import { useState } from 'react'
import Link from '../components/LocalizedLink'
import { Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

// Density in g/cm³ and an example price per kg. The prices are only starting
// values for the form; visitors are expected to type in their own.
const materials = {
  steel: { density: 7.85, pricePerKg: 1.2 },
  stainless: { density: 8.0, pricePerKg: 3.8 },
  aluminum: { density: 2.7, pricePerKg: 4.0 },
}

const defaults = {
  material: 'steel',
  length: '500',
  width: '300',
  thickness: '2',
  quantity: '10',
  pricePerKg: String(materials.steel.pricePerKg),
  scrap: '15',
  innerCut: '400',
  cutRate: '1.00',
  bends: '2',
  bendRate: '0.50',
  setup: '25',
  markup: '25',
}

const translations = {
  el: {
    title: 'Δωρεάν Υπολογιστής Κόστους Λαμαρίνας',
    subtitle: 'Υπολογίστε σε δευτερόλεπτα το κόστος και την τιμή πώλησης ενός εξαρτήματος λαμαρίνας: υλικό, κοπή, στράντζα, σετάρισμα και κέρδος.',
    inputs: 'Στοιχεία εξαρτήματος',
    rates: 'Οι τιμές σας',
    results: 'Αποτέλεσμα ανά τεμάχιο',
    materialNames: { steel: 'Χάλυβας', stainless: 'Ανοξείδωτος', aluminum: 'Αλουμίνιο' },
    fields: {
      material: 'Υλικό',
      length: 'Μήκος ανάπτυξης (mm)',
      width: 'Πλάτος ανάπτυξης (mm)',
      thickness: 'Πάχος (mm)',
      quantity: 'Ποσότητα',
      innerCut: 'Εσωτερικές κοπές / οπές (mm)',
      bends: 'Αριθμός στραντζαρισμάτων',
      pricePerKg: 'Τιμή υλικού (€/kg)',
      scrap: 'Φύρα (%)',
      cutRate: 'Κόστος κοπής (€/m)',
      bendRate: 'Κόστος ανά στραντζάρισμα (€)',
      setup: 'Σετάρισμα ανά παρτίδα (€)',
      markup: 'Κέρδος (%)',
    },
    out: {
      weight: 'Βάρος',
      cutLength: 'Μήκος κοπής',
      material: 'Υλικό',
      cutting: 'Κοπή',
      bending: 'Στράντζα',
      setup: 'Σετάρισμα',
      cost: 'Κόστος ανά τεμάχιο',
      price: 'Τιμή πώλησης ανά τεμάχιο',
      total: 'Σύνολο παραγγελίας',
    },
    note: 'Οι προεπιλεγμένες τιμές είναι ενδεικτικές. Βάλτε τις τιμές του δικού σας εργαστηρίου για ρεαλιστικό αποτέλεσμα.',
    howTitle: 'Πώς υπολογίζεται',
    how: [
      'Βάρος = μήκος × πλάτος × πάχος × πυκνότητα.',
      'Υλικό = βάρος × (1 + φύρα) × τιμή ανά κιλό.',
      'Κοπή = (περίμετρος + εσωτερικές κοπές) × κόστος ανά μέτρο.',
      'Στράντζα = αριθμός στραντζαρισμάτων × κόστος ανά στραντζάρισμα.',
      'Σετάρισμα = κόστος σεταρίσματος ÷ ποσότητα.',
      'Τιμή πώλησης = κόστος × (1 + κέρδος).',
    ],
    upsellTitle: 'Θέλετε αυτό αυτόματα από το σχέδιο;',
    upsell: 'Ο υπολογιστής χρησιμοποιεί σταθερό κόστος ανά μέτρο κοπής. Το Workshop Assistant διαβάζει τις διαστάσεις, τις οπές και τα στραντζαρίσματα από DXF ή STEP, υπολογίζει τον χρόνο κοπής ανά υλικό και πάχος και βγάζει προσφορά Excel για όλα τα εξαρτήματα μαζί.',
    upsellBtn: 'Δωρεάν Δοκιμή',
    guide: 'Αναλυτικός οδηγός κοστολόγησης λαμαρίνας',
    faq: [
      { q: 'Πώς υπολογίζω το κόστος ενός εξαρτήματος λαμαρίνας;', a: 'Προσθέστε το κόστος υλικού (βάρος × τιμή ανά κιλό, μαζί με τη φύρα), το κόστος κοπής (μήκος κοπής × κόστος ανά μέτρο), το κόστος στράντζας και το σετάρισμα μοιρασμένο στην ποσότητα. Μετά προσθέστε το κέρδος σας.' },
      { q: 'Είναι δωρεάν ο υπολογιστής;', a: 'Ναι, ο υπολογιστής είναι δωρεάν και λειτουργεί στον browser χωρίς εγγραφή.' },
      { q: 'Γιατί διαφέρει από το Workshop Assistant;', a: 'Ο υπολογιστής χρησιμοποιεί ένα σταθερό κόστος ανά μέτρο κοπής. Το Workshop Assistant υπολογίζει τον χρόνο κοπής βάσει υλικού, πάχους και αντοχής, και παίρνει τη γεωμετρία απευθείας από το αρχείο CAD.' },
    ],
  },
  en: {
    title: 'Free Sheet Metal Cost Calculator',
    subtitle: 'Work out the cost and selling price of a sheet metal part in seconds: material, cutting, bending, setup and margin.',
    inputs: 'Part details',
    rates: 'Your rates',
    results: 'Result per part',
    materialNames: { steel: 'Steel', stainless: 'Stainless steel', aluminum: 'Aluminum' },
    fields: {
      material: 'Material',
      length: 'Flat length (mm)',
      width: 'Flat width (mm)',
      thickness: 'Thickness (mm)',
      quantity: 'Quantity',
      innerCut: 'Internal cuts / holes (mm)',
      bends: 'Number of bends',
      pricePerKg: 'Material price (€/kg)',
      scrap: 'Scrap (%)',
      cutRate: 'Cutting cost (€/m)',
      bendRate: 'Cost per bend (€)',
      setup: 'Setup per batch (€)',
      markup: 'Margin (%)',
    },
    out: {
      weight: 'Weight',
      cutLength: 'Cut length',
      material: 'Material',
      cutting: 'Cutting',
      bending: 'Bending',
      setup: 'Setup',
      cost: 'Cost per part',
      price: 'Selling price per part',
      total: 'Order total',
    },
    note: 'The default values are examples only. Enter your own shop rates for a realistic result.',
    howTitle: 'How it is calculated',
    how: [
      'Weight = length × width × thickness × density.',
      'Material = weight × (1 + scrap) × price per kg.',
      'Cutting = (perimeter + internal cuts) × cost per metre.',
      'Bending = number of bends × cost per bend.',
      'Setup = setup cost ÷ quantity.',
      'Selling price = cost × (1 + margin).',
    ],
    upsellTitle: 'Want this done automatically from the drawing?',
    upsell: 'This calculator uses a flat cost per metre of cut. Workshop Assistant reads dimensions, holes and bends from DXF or STEP files, works out cutting time by material and thickness, and produces an Excel quote for all your parts at once.',
    upsellBtn: 'Download Free Trial',
    guide: 'Full guide to sheet metal costing',
    faq: [
      { q: 'How do I calculate the cost of a sheet metal part?', a: 'Add the material cost (weight × price per kg, plus scrap), the cutting cost (cut length × cost per metre), the bending cost and the setup cost spread over the quantity. Then add your margin.' },
      { q: 'Is this cost calculator free?', a: 'Yes, the calculator is free and runs in your browser with no sign-up.' },
      { q: 'Why does it differ from Workshop Assistant?', a: 'The calculator uses a flat cost per metre of cut. Workshop Assistant calculates cutting time from material, thickness and strength, and takes the geometry directly from the CAD file.' },
    ],
  },
}

const num = (v) => {
  const n = parseFloat(String(v).replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : 0
}

const CostCalculator = () => {
  const { lang } = useLanguage()
  const t = translations[lang]
  const [form, setForm] = useState(defaults)

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const setMaterial = (e) => {
    const material = e.target.value
    setForm((f) => ({ ...f, material, pricePerKg: String(materials[material].pricePerKg) }))
  }

  const L = num(form.length)
  const W = num(form.width)
  const T = num(form.thickness)
  const qty = Math.max(1, Math.round(num(form.quantity)) || 1)
  const weight = (L * W * T * materials[form.material].density) / 1e6
  const cutLength = (2 * (L + W) + num(form.innerCut)) / 1000
  const material = weight * (1 + num(form.scrap) / 100) * num(form.pricePerKg)
  const cutting = cutLength * num(form.cutRate)
  const bending = Math.round(num(form.bends)) * num(form.bendRate)
  const setup = num(form.setup) / qty
  const cost = material + cutting + bending + setup
  const price = cost * (1 + num(form.markup) / 100)

  const locale = lang === 'el' ? 'el-GR' : 'en-GB'
  const eur = (v) => v.toLocaleString(locale, { style: 'currency', currency: 'EUR' })
  const fixed = (v, d) => v.toLocaleString(locale, { minimumFractionDigits: d, maximumFractionDigits: d })

  const field = (key) => (
    <label key={key} className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{t.fields[key]}</span>
      <input type="number" inputMode="decimal" min="0" step="any" value={form[key]} onChange={set(key)} className="input-field" />
    </label>
  )

  const rows = [
    [t.out.material, eur(material)],
    [t.out.cutting, eur(cutting)],
    [t.out.bending, eur(bending)],
    [t.out.setup, eur(setup)],
  ]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Free Manufacturing Cost Calculator for Sheet Metal Parts"
        description="Free online cost calculator for sheet metal parts: enter size, thickness, cuts and bends to get material, cutting, bending and setup cost plus selling price per part."
        path="/cost-calculator"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h1>
            <p className="text-xl opacity-90">{t.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 card space-y-8">
            <div>
              <h2 className="text-2xl font-bold mb-4">{t.inputs}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">{t.fields.material}</span>
                  <select value={form.material} onChange={setMaterial} className="input-field">
                    {Object.keys(materials).map((m) => (
                      <option key={m} value={m}>{t.materialNames[m]}</option>
                    ))}
                  </select>
                </label>
                {['thickness', 'length', 'width', 'innerCut', 'bends', 'quantity'].map(field)}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4">{t.rates}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {['pricePerKg', 'scrap', 'cutRate', 'bendRate', 'setup', 'markup'].map(field)}
              </div>
              <p className="text-sm text-gray-500 mt-4">{t.note}</p>
            </div>
          </div>

          <div className="card h-fit lg:sticky lg:top-24" aria-live="polite">
            <h2 className="text-2xl font-bold mb-4">{t.results}</h2>
            <dl className="space-y-2 text-gray-600">
              <div className="flex justify-between"><dt>{t.out.weight}</dt><dd>{fixed(weight, 2)} kg</dd></div>
              <div className="flex justify-between"><dt>{t.out.cutLength}</dt><dd>{fixed(cutLength, 2)} m</dd></div>
              <hr className="my-3" />
              {rows.map(([label, value]) => (
                <div key={label} className="flex justify-between"><dt>{label}</dt><dd>{value}</dd></div>
              ))}
              <hr className="my-3" />
              <div className="flex justify-between font-semibold text-gray-900"><dt>{t.out.cost}</dt><dd>{eur(cost)}</dd></div>
              <div className="flex justify-between font-bold text-primary-600 text-xl"><dt>{t.out.price}</dt><dd>{eur(price)}</dd></div>
              <div className="flex justify-between font-semibold text-gray-900"><dt>{t.out.total} ({qty})</dt><dd>{eur(price * qty)}</dd></div>
            </dl>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">{t.howTitle}</h2>
          <ul className="list-disc pl-6 space-y-2 text-lg text-gray-600">
            {t.how.map((line) => <li key={line}>{line}</li>)}
          </ul>
          <Link to="/sheet-metal-costing" className="inline-flex items-center gap-2 mt-6 font-semibold text-primary-600 hover:text-primary-700">
            {t.guide}<ArrowRight className="h-4 w-4" />
          </Link>

          <h2 className="text-3xl font-bold mt-16 mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.upsellTitle}</h2>
          <p className="text-xl mb-8 opacity-90">{t.upsell}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.upsellBtn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default CostCalculator
